// copied from https://github.com/ClearScore/south-africa-national-id-validation
// because they have no typescript bindings and we want the extracted metadata in the response
/*
 * ZA National Id Validation
 * Based of: https://www.westerncape.gov.za/general-publication/decoding-your-south-african-id-number-0
 * ----------------------------------
 * Validation rules:
 *  13 - digit number
 *  {6} (YYMMDD) date of birth definition
 *  {4} (SSSS) used to define your gender
 *      Females are assigned numbers in the range 0000 - 4999 and males from 5000 - 9999
 *  {1} (C) classify citizenship
 *      SA citizen: 0, Permanent resident: 1
 *  {1} no longer used
 *  {1} checksum digit for verification
 */

/*
        number              | valid    | type
        ${'9202295029188'}  | ${true}  | ${'valid'}
        ${'7311190013080'}  | ${true}  | ${'valid'}
        ${'6512230302083'}  | ${true}  | ${'valid'}
        ${'7311190T13080'}  | ${false} | ${'invalid format'}
        ${'9202295029187'}  | ${false} | ${'invalid checksum'}
        ${'8813210302087'}  | ${false} | ${'invalid date'}
        ${'9302295029087'}  | ${false} | ${'invalid date'}
        ${'1806110013082'}  | ${false} | ${'too young'}
        ${'6512230302281'}  | ${false} | ${'invalid citizenship'}
        ${'73111900130805'} | ${false} | ${'too many digits'}
        ${'651223030206'}   | ${false} | ${'too few digits'}
        ${''}               | ${false} | ${'blank'}
 */
// Uses the Luhn Algorithm
// Adapted from https://github.com/tiaanduplessis/mod10-check-digit/blob/master/index.js
const mod10CheckDigit = ({ number }: { number: string }) => {
  const doubledSum = number
    .split("")
    .map((val, index) => {
      if ((index + number.length) % 2 !== 0) {
        const double = Number.parseInt(val, 10) * 2;
        return double > 9 ? double - 9 : double;
      }

      return Number.parseInt(val);
    })
    .reduce((acc, curr) => acc + curr);

  return (doubledSum * 9) % 10;
};

// Based on date-fns answer: https://github.com/date-fns/date-fns/issues/800#issuecomment-403581282
const isValidDate = (dateString: string) => {
  const dateObject = new Date(dateString);
  const [year, month, day] = dateString.split("/");

  // need to reduce month value by 1 to accommodate new Date formats the month
  const formattedMonth = Number.parseInt(month) - 1;
  if (
    dateObject.getFullYear() === Number.parseInt(year) &&
    dateObject.getMonth() === formattedMonth &&
    dateObject.getDate() === Number.parseInt(day)
  ) {
    return true;
  }
  return false;
};

const NATIONAL_ID_VALID_LENGTH = 13;

export interface ValidateResponse {
  error?: "short" | "long" | "format" | "date" | "age" | "checksum" | undefined;
  dob?: string;
  age?: number;
  gender?: "male" | "female";
}
export function validateNationalIdNumber({
  number,
  minAge = 18,
}: {
  number: string;
  minAge?: number;
}): ValidateResponse {
  if (number.length < NATIONAL_ID_VALID_LENGTH) {
    return { error: "short" };
  }

  if (number.length > NATIONAL_ID_VALID_LENGTH) {
    return { error: "long" };
  }
  // Basic format check first
  // -----------------------------------------
  const re = /^\d{2}[0-1][0-9][0-3]\d\d{4}[0-1]\d{2}$/;
  if (!re.test(number)) {
    return { error: "format" };
  }

  // Now the date checks
  // -----------------------------------------
  const currentYear = new Date().getFullYear().toString();
  // Dob year is only YY, so we need to guess whether the user is born in 2000s or 1900s
  const century =
    number.substring(0, 2) < currentYear.substring(2, 4) ? "20" : "19";
  const year = Number.parseInt(`${century}${number.substring(0, 2)}`);
  const month = number.substring(2, 4);
  const day = number.substring(4, 6);
  // Check if valid date
  if (!isValidDate(`${year}/${month}/${day}`)) {
    return { error: "date" };
  }
  const dob = `${year}-${month}-${day}`;
  // Check if user in age rage
  const age = Number.parseInt(currentYear) - year;
  if (age < minAge) {
    return { error: "age" };
  }

  const genderParam = parseInt(number.substring(6, 10));
  const gender =
    genderParam >= 0 && genderParam <= 4999
      ? "female"
      : genderParam >= 5000 && genderParam <= 9999
        ? "male"
        : undefined;

  // Lastly the CheckSum
  // -----------------------------------------
  const numberToValidate = number.substring(0, 12);
  const checksumDigit = Number.parseInt(number.substring(12, 13), 10);

  if (mod10CheckDigit({ number: numberToValidate }) !== checksumDigit) {
    return { error: "checksum" };
  }

  return { age, dob, gender };
}
